import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReportShell, { reportStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import ReportOverlay from '../../../shared/ReportOverlay';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import './IntegrityReportPage.css';

// Its own screen, own local parameter panel (rule checklist + Category
// scope picker); results presented as an overlay (ReportOverlay +
// ReportShell), same as every other report in the app. Deviates from
// pat-architecture-reports-ui-standard.md's original catalog-report spec
// ("content renders below") — flagged there for a decision-manager
// amendment. First occupant of the 'inventory-reports' nav stub
// (vis_integrity_reports.md, Group 0).
function IntegrityReportPage() {
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [checkedRuleKeys, setCheckedRuleKeys] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/product-categories').then((res) => setCategories(res.data.data || []));
    axios.get('/api/integrity-reports/rules').then((res) => setRules(res.data.data || []));
  }, []);

  const categoryOptions = categories.filter((c) => c.is_active);
  const selectedCategory = categories.find((c) => String(c.id) === String(categoryId));
  const canRun = categoryId !== '' && checkedRuleKeys.length > 0;

  function toggleRule(key) {
    setCheckedRuleKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  async function handleRun() {
    setLoading(true);
    try {
      const res = await axios.post('/api/integrity-reports/run', { categoryId, ruleKeys: checkedRuleKeys });
      setResults(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Failed to run integrity report');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: 900, margin: '32px auto 48px' }}>
      <div style={{ background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, maxWidth: 480 }}>
        <h2 style={{ margin: '0 0 16px', fontWeight: 600, fontSize: 20 }}>Product Integrity</h2>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Checks</div>
          {rules.length === 0 && <div style={{ color: '#888', fontSize: 13 }}>No integrity checks are registered yet.</div>}
          {rules.map((rule) => (
            <label key={rule.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={checkedRuleKeys.includes(rule.key)}
                onChange={() => toggleRule(rule.key)}
              />
              {rule.label}
            </label>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Category</div>
          <select className="styled-input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} style={{ width: 260 }}>
            <option value="">Select a category…</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>{'\u00A0\u00A0\u00A0'.repeat(c.depth || 0)}{c.name}</option>
            ))}
          </select>
          <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>Includes all active sub-categories.</div>
        </div>

        <button type="button" className="integrity-report-run" onClick={handleRun} disabled={!canRun || loading}>
          {loading ? 'Running…' : 'Run'}
        </button>
        <button type="button" className="integrity-report-cancel" onClick={() => navigate('/inventory-dashboard')}>
          Cancel
        </button>
      </div>

      {results && (
        <ReportOverlay onClose={() => setResults(null)}>
          <ReportShell
            title="Product Integrity Report"
            subtitle={(
              <>
                <div>{formatReportTimestamp()}</div>
                <div>Category: {selectedCategory ? selectedCategory.name : ''} (+ descendants)</div>
              </>
            )}
            onClose={() => setResults(null)}
          >
            {results.map((section) => (
              <div key={section.key} style={{ marginBottom: 24 }}>
                <h3 style={{ marginBottom: 8 }}>{section.label}</h3>
                {section.flagged.length === 0 ? (
                  <div style={{ color: '#888', fontSize: 14 }}>No issues found.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize }}>
                    <thead>
                      <tr className="print-only-row"><td colSpan={5} style={{ height: 18, padding: 0, border: 'none' }} /></tr>
                      <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                        <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
                        <th style={{ padding: '6px 8px 6px 0' }}>Name</th>
                        <th style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>Product Type</th>
                        <th style={{ padding: '6px 8px 6px 0' }}>Rule</th>
                        <th style={{ padding: '6px 8px 6px 0' }}>Reason</th>
                      </tr>
                    </thead>
                    <tfoot>
                      <tr className="print-only-row"><td colSpan={5} style={{ height: 20, padding: 0, border: 'none' }} /></tr>
                    </tfoot>
                    <tbody>
                      {section.flagged.map((row) => (
                        <tr key={row.sku} style={{ borderBottom: '1px solid #ddd', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                          <td style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>{row.sku}</td>
                          <td style={{ padding: '6px 8px 6px 0' }}>{row.name}</td>
                          <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>{row.product_type || ''}</td>
                          <td style={{ padding: '6px 8px 6px 0' }}>{section.label}</td>
                          <td style={{ padding: '6px 8px 6px 0' }}>{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </ReportShell>
        </ReportOverlay>
      )}
    </div>
  );
}

export default IntegrityReportPage;
